import React, { useEffect, useMemo, useState } from "react";
import api from "../api/axios";
import PageHeader from "../components/PageHeader";
import BrainImpactViewer from "../components/BrainImpactViewer";
import NeuronImpactMap from "../components/NeuronImpactMap";
import "./BrainMapPage.responsive.css";

const DEFAULT_ANALYSIS = {
  raw_text: "",
  structured: {
    impacted_regions: [
      {
        id: "frontal_lobe",
        label: "Frontal Lobe",
        why: "Executive stress, emotional regulation, and decision overload cues were detected.",
        intensity: "moderate",
        color: "#6366f1",
      },
      {
        id: "amygdala",
        label: "Amygdala",
        why: "Panic, fear response, and anxiety-linked emotional activation cues were found.",
        intensity: "high",
        color: "#f59e0b",
      },
      {
        id: "hippocampus",
        label: "Hippocampus",
        why: "Memory and stress-related pattern mapping suggests supportive monitoring.",
        intensity: "low",
        color: "#16a34a",
      },
    ],
  },
};

export default function BrainMapPage() {
  const [patients, setPatients] = useState([]);
  const [sessions, setSessions] = useState([]);

  const [loadingPatients, setLoadingPatients] = useState(true);
  const [loadingSessions, setLoadingSessions] = useState(false);

  const [selectedPatientId, setSelectedPatientId] = useState("all");
  const [selectedSessionId, setSelectedSessionId] = useState("");
  const [viewMode, setViewMode] = useState("anatomy");

  const [analysisData, setAnalysisData] = useState(DEFAULT_ANALYSIS);

  useEffect(() => {
    loadPatients();
  }, []);

  useEffect(() => {
    if (selectedPatientId !== "all") {
      loadSessions(selectedPatientId);
    } else {
      setSessions([]);
      setSelectedSessionId("");
      setAnalysisData(DEFAULT_ANALYSIS);
    }
  }, [selectedPatientId]);

  useEffect(() => {
    if (selectedSessionId) {
      loadSessionDetail(selectedSessionId);
    } else {
      setAnalysisData(DEFAULT_ANALYSIS);
    }
  }, [selectedSessionId]);

  const loadPatients = async () => {
    try {
      setLoadingPatients(true);
      const res = await api.get("/api/patient/");
      setPatients(res.data || []);
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not load patients");
    } finally {
      setLoadingPatients(false);
    }
  };

  const loadSessions = async (patientId) => {
    try {
      setLoadingSessions(true);
      const res = await api.get(`/api/session/patient/${patientId}`);
      setSessions(res.data || []);
      setSelectedSessionId("");
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not load sessions");
    } finally {
      setLoadingSessions(false);
    }
  };

  const loadSessionDetail = async (sessionId) => {
    try {
      const res = await api.get(`/api/session/detail/${sessionId}`);
      const analysis = res.data?.analysis;

      if (!analysis) {
        setAnalysisData(DEFAULT_ANALYSIS);
        return;
      }

      const fallbackRegions =
        analysis?.structured?.impacted_regions?.length > 0
          ? analysis.structured.impacted_regions
          : DEFAULT_ANALYSIS.structured.impacted_regions;

      setAnalysisData({
        ...analysis,
        structured: {
          ...analysis.structured,
          impacted_regions: fallbackRegions,
        },
      });
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not load brain map analysis");
      setAnalysisData(DEFAULT_ANALYSIS);
    }
  };

  const selectedPatient = useMemo(() => {
    return patients.find((p) => String(p.id) === String(selectedPatientId)) || null;
  }, [patients, selectedPatientId]);

  const selectedSession = useMemo(() => {
    return sessions.find((s) => String(s.id) === String(selectedSessionId)) || null;
  }, [sessions, selectedSessionId]);

  const impactedRegions =
    analysisData?.structured?.impacted_regions || DEFAULT_ANALYSIS.structured.impacted_regions;

  return (
    <>
      <PageHeader
        eyebrow="Brain Map"
        title="Neuro Impact Workspace"
        subtitle="Review AI-inferred brain hotspots in anatomy mode now, with future network mode ready for neuron mapping."
        actions={
          <div className="page-header-actions">
            <select
              className="filter-select"
              value={selectedPatientId}
              onChange={(e) => setSelectedPatientId(e.target.value)}
              disabled={loadingPatients}
            >
              <option value="all">Select patient</option>
              {patients.map((patient) => (
                <option key={patient.id} value={String(patient.id)}>
                  {patient.name}
                </option>
              ))}
            </select>

            <select
              className="filter-select"
              value={selectedSessionId}
              onChange={(e) => setSelectedSessionId(e.target.value)}
              disabled={!selectedPatient || loadingSessions}
            >
              <option value="">Select session</option>
              {sessions.map((session) => (
                <option key={session.id} value={String(session.id)}>
                  {session.title || `Session #${session.id}`}
                </option>
              ))}
            </select>

            <select
              className="filter-select"
              value={viewMode}
              onChange={(e) => setViewMode(e.target.value)}
            >
              <option value="anatomy">Anatomy Mode</option>
              <option value="network">Network Mode</option>
            </select>
          </div>
        }
      />

      {viewMode === "network" ? (
        <div className="card empty-card">
          <h3>Network mode is queued next</h3>
          <p className="muted">
            Anatomy mode is live now. Sigma/D3 neuron topology mode can be added next without disturbing this page structure.
          </p>
        </div>
      ) : (
        <div className="brain-page-shell">
          <div className="card brain-page-meta-card">
            <div className="summary-grid compact-summary-grid">
              <div className="summary-item">
                <span>Patient</span>
                <strong>{selectedPatient?.name || "Not selected"}</strong>
              </div>
              <div className="summary-item">
                <span>Session</span>
                <strong>{selectedSession?.title || "Not selected"}</strong>
              </div>
              <div className="summary-item">
                <span>Hotspots</span>
                <strong>{impactedRegions.length}</strong>
              </div>
              <div className="summary-item">
                <span>Mode</span>
                <strong>{viewMode === "anatomy" ? "Anatomy View" : "Network View"}</strong>
              </div>
            </div>
          </div>

          <NeuronImpactMap analysis={analysisData} />

          <div className="card brain-source-card">
            <h3>Clinical Note</h3>
            <p className="muted">
              Brain hotspots shown here are AI-inferred clinical support regions, not scan-confirmed radiology findings.
              Use them to guide review and discussion, not as a standalone diagnosis.
            </p>
          </div>
        </div>
      )}
    </>
  );
}